class EmarsysEmailSignup extends HTMLElement {
  connectedCallback() {
    this.actionSelect = this.querySelector('[data-action-select]');
    this.additionalInputs = this.querySelector('[data-additional-inputs]');
    this.form = this.querySelector('form');
    if (!this.actionSelect || !this.additionalInputs || !this.form) return;

    this.actionSelect.addEventListener('change', () => this.syncVisibility());
    this.form.addEventListener('submit', (event) => this.onSubmitHandler(event));

    // Mark all fields the moment the submit button is clicked (covers button click + keyboard Enter)
    const submitBtn = this.form.querySelector('[type="submit"]');
    if (submitBtn) {
      submitBtn.addEventListener('click', () => this._markAllFieldStates());
    }

    // Live re-validation: update a field's state after the user changes it (only once active)
    this.form.querySelectorAll('input:not([type="checkbox"]), select').forEach((el) => {
      if (!(el instanceof HTMLInputElement || el instanceof HTMLSelectElement)) return;
      const update = () => {
        const wrapper = el.closest('.emarsys-email-signup__input-wrapper');
        if (!(wrapper instanceof HTMLElement)) return;
        if (wrapper.getAttribute('data-validation-active') !== 'true') return;
        this._updateFieldState(wrapper, el.validity.valid);
      };
      el.addEventListener('input', update);
      el.addEventListener('change', update);
    });

    this.form.querySelectorAll('input[type="checkbox"]').forEach((el) => {
      if (!(el instanceof HTMLInputElement)) return;
      el.addEventListener('change', () => {
        const wrapper = el.closest('.emarsys-email-signup__checkbox-wrapper');
        if (!(wrapper instanceof HTMLElement)) return;
        if (wrapper.getAttribute('data-validation-active') !== 'true') return;
        this._updateCheckboxState(wrapper, el.validity.valid);
      });
    });

    this.syncVisibility();
  }

  syncVisibility() {
    if (!(this.actionSelect instanceof HTMLSelectElement) || !(this.additionalInputs instanceof HTMLElement)) return;

    const isUnsubscribe = this.actionSelect.value === 'unsubscribe';

    if (isUnsubscribe) {
      this.additionalInputs.style.display = 'none';
      this.additionalInputs.querySelectorAll('[required]').forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        el.dataset.wasRequired = 'true';
        el.removeAttribute('required');
      });
    } else {
      this.additionalInputs.style.display = '';
      this.additionalInputs.querySelectorAll('[data-was-required]').forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        el.setAttribute('required', '');
        delete el.dataset.wasRequired;
      });
    }
  }

  /** @param {SubmitEvent} event */
  onSubmitHandler(event) {
    // Native HTML5 validation already ran — if we're here, all required fields are valid
    event.preventDefault();
    if (!this.form) return;

    const formData = new FormData(this.form);

    // Collect date field groups that need to be combined into YYYY-MM-DD
    const skipFields = new Set();
    /** @type {Array<{day: string, month: string, year: string|null, target: string}>} */
    const dateCombines = [];

    this.form.querySelectorAll('[data-date-target]').forEach((el) => {
      const htmlEl = /** @type {HTMLElement} */ (el);
      const day = htmlEl.dataset.dateDay;
      const month = htmlEl.dataset.dateMonth;
      const year = htmlEl.dataset.dateYear || null;
      const target = htmlEl.dataset.dateTarget;
      if (!day || !month || !target) return;
      dateCombines.push({ day, month, year, target });
      skipFields.add(day);
      skipFields.add(month);
      if (year) skipFields.add(year);
    });

    /** @type {Record<string, any>} */
    const payload = {};

    for (const [key, value] of formData.entries()) {
      if (typeof value !== 'string' || value.trim() === '') continue;
      if (skipFields.has(key)) continue;
      this._setNestedValue(payload, this._normalizePayloadKey(key), value);
    }

    // Combine date parts into YYYY-MM-DD
    for (const { day, month, year, target } of dateCombines) {
      const d = formData.get(day);
      const m = formData.get(month);
      const y = year ? formData.get(year) : null;
      if (typeof d !== 'string' || typeof m !== 'string' || typeof y !== 'string' || !d || !m || !y) continue;
      const combined = `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
      this._setNestedValue(payload, target, combined);
    }

    const endpoint = this.form.getAttribute('action');
    if (!endpoint) {
      console.warn('Emarsys: no form action URL configured.');
      return;
    }

    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        this.dispatchEvent(new CustomEvent('emarsys:success', { detail: data, bubbles: true }));
        const isUnsubscribe = this.actionSelect instanceof HTMLSelectElement && this.actionSelect.value === 'unsubscribe';
        this._showSuccess(isUnsubscribe);
      })
      .catch((err) => {
        this.dispatchEvent(new CustomEvent('emarsys:error', { detail: err, bubbles: true }));
        this._showError();
      });
  }

  _markAllFieldStates() {
    if (!this.form) return;
    this.form.querySelectorAll('input:not([type="checkbox"]), select').forEach((el) => {
      if (!(el instanceof HTMLInputElement || el instanceof HTMLSelectElement)) return;
      const wrapper = el.closest('.emarsys-email-signup__input-wrapper');
      if (wrapper instanceof HTMLElement) this._updateFieldState(wrapper, el.validity.valid);
    });
    this.form.querySelectorAll('input[type="checkbox"]').forEach((el) => {
      if (!(el instanceof HTMLInputElement)) return;
      const wrapper = el.closest('.emarsys-email-signup__checkbox-wrapper');
      if (wrapper instanceof HTMLElement) this._updateCheckboxState(wrapper, el.validity.valid);
    });
  }

  /**
   * @param {HTMLElement} wrapper
   * @param {boolean} isValid
   */
  _updateFieldState(wrapper, isValid) {
    wrapper.setAttribute('valid', isValid ? 'true' : 'false');
    wrapper.setAttribute('data-validation-active', 'true');
    this._setValidationIcon(wrapper, isValid ? 'checkmark' : 'error');
  }

  /**
   * @param {HTMLElement} wrapper
   * @param {boolean} isValid
   */
  _updateCheckboxState(wrapper, isValid) {
    wrapper.setAttribute('valid', isValid ? 'true' : 'false');
    wrapper.setAttribute('data-validation-active', 'true');
  }

  /**
   * @param {HTMLElement} wrapper
   * @param {'checkmark' | 'error'} iconType
   */
  _setValidationIcon(wrapper, iconType) {
    const iconEl = wrapper.querySelector('.emarsys-email-signup__validation-icon');
    if (!(iconEl instanceof HTMLElement)) return;
    iconEl.innerHTML =
      iconType === 'checkmark'
        ? `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4.75439 10.7485L7.68601 14.5888C7.79288 14.7288 7.84632 14.7988 7.91174 14.8242C7.96907 14.8466 8.03262 14.8469 8.09022 14.8253C8.15596 14.8007 8.21026 14.7314 8.31886 14.5927L15.2475 5.74658" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg>`
        : `<svg class="icon icon-error" viewBox="0 0 13 13"><circle cx="6.5" cy="6.5" r="5.5" stroke="#fff" stroke-width="var(--icon-stroke-width)"/><circle cx="6.5" cy="6.5" r="5.5" fill="#EB001B" stroke="#EB001B" stroke-width=".7"/><path fill="#fff" d="m5.874 3.528.1 4.044h1.053l.1-4.044zm.627 6.133c.38 0 .68-.288.68-.656s-.3-.656-.68-.656-.681.288-.681.656.3.656.68.656"/><path fill="#fff" stroke="#EB001B" stroke-width=".7" d="M5.874 3.178h-.359l.01.359.1 4.044.008.341h1.736l.008-.341.1-4.044.01-.359H5.873Zm.627 6.833c.56 0 1.03-.432 1.03-1.006s-.47-1.006-1.03-1.006-1.031.432-1.031 1.006.47 1.006 1.03 1.006Z"/></svg>`;
  }

  /** @param {boolean} [isUnsubscribe] */
  _showSuccess(isUnsubscribe) {
    if (this.form) this.form.hidden = true;
    const selector = isUnsubscribe ? '[data-unsubscribe-success-message]' : '[data-success-message]';
    const msg = this.querySelector(selector);
    if (msg instanceof HTMLElement) msg.removeAttribute('hidden');
  }

  _showError() {
    if (this.form) this.form.hidden = true;
    const msg = this.querySelector('[data-error-message]');
    if (msg instanceof HTMLElement) msg.removeAttribute('hidden');
  }

  /**
   * Maps Shopify-standard contact[email] to email so the JSON payload stays { email }.
   * @param {string} key
   * @returns {string}
   */
  _normalizePayloadKey(key) {
    if (key === 'contact[email]' || key === 'contact.email') return 'email';
    return key;
  }

  /**
   * Sets a value at a dot-notated path, e.g. "metafields.custom.birth" → { metafields: { custom: { birth: value } } }
   * @param {Record<string, any>} obj
   * @param {string} path
   * @param {string} value
   */
  _setNestedValue(obj, path, value) {
    const keys = path.split('.');
    const lastKey = keys.pop();
    if (!lastKey) return;
    let cursor = obj;
    for (const key of keys) {
      if (typeof cursor[key] !== 'object' || cursor[key] === null) {
        cursor[key] = {};
      }
      cursor = cursor[key];
    }
    cursor[lastKey] = value;
  }
}

if (!customElements.get('emarsys-email-signup')) {
  customElements.define('emarsys-email-signup', EmarsysEmailSignup);
}
